/**
 * This source code is quoted from rc-slider.
 * homepage: https://github.com/react-component/slider
 */
import classNames from 'classnames';
// import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import createSlider from './common/createSlider';
// import shallowEqual from './common/shallowequal';
import shallowEqual from 'shallowequal';
import Track from './common/Track';
import Handle from './Handle';
import * as utils from './utils';
import type {RangeProps, RangeState} from './iSlider'

// 默认属性
const defaultProps = {
    count: 1,
    allowCross: true,
    pushable: false,
};

const Range = forwardRef<any, RangeProps>((props, ref) => {
    // 合并默认属性
    const mergedProps = {...defaultProps, ...props};
    const {count, min = 0, max = 100} = mergedProps;

    // 从 props 中提取 createSlider HOC 注入的方法
    const {
        calcValueByPos: injectedCalcValueByPos,
        calcOffset: injectedCalcOffset,
        removeDocumentEvents: injectedRemoveDocumentEvents
    } = props as any;

    // Refs for storing instance variables
    const _getPointsCacheRef = useRef<{ marks: RangeProps['marks']; step: number | null; points: number[] }>();
    const handleRefs = useRef<any[]>([]);

    // 这些方法会被 createSlider HOC 注入
    const calcValueByPosRef = useRef<(value: number) => number>(injectedCalcValueByPos || (() => 0));
    const calcOffsetRef = useRef<(value: number) => number>(injectedCalcOffset || (() => 0));
    const removeDocumentEventsRef = useRef<() => void>(injectedRemoveDocumentEvents || (() => {}));

    // 初始化值的计算 - 移到 useState 之前
    const getInitialBoundsValue = () => {
        const initialValue = Array.apply(null, Array(count + 1))
            .map(() => min);
        const defaultValue = 'defaultValue' in mergedProps ?
            mergedProps.defaultValue : initialValue;
        const value = (mergedProps.value !== undefined ?
            mergedProps.value : defaultValue) as number[];
        return value.map(v => {
            const valInRange = utils.ensureValueInRange(v, mergedProps);
            return utils.ensureValuePrecision(valInRange, mergedProps);
        });
    };

    // 先定义状态（用于 ensureValueNotConflict）
    const [bounds, setBounds] = useState<number[]>(() => getInitialBoundsValue());
    const [handle, setHandle] = useState<number | null>(null);
    const [recent, setRecent] = useState<number>(() => {
        const initialBounds = getInitialBoundsValue();
        return initialBounds[0] === max ? 0 : initialBounds.length - 1;
    });

    // ensureValueNotConflict 函数（需要先定义，因为 trimAlignValue 依赖它）
    const ensureValueNotConflict = useCallback((val: number, config: RangeProps, currentHandle: number | null, currentBounds: number[]) => {
	    /* eslint-disable eqeqeq */
	    if (!config.allowCross && currentHandle != null) {
	        // 最右边的点只能执行这个，因为handle=bounds.length-1；中间点也走这个判断
	        if (currentHandle > 0 && val <= currentBounds[currentHandle - 1]) {
	            return currentBounds[currentHandle - 1];
	        }
	        // 最左边的点只能执行这个，因为handle=0；中间点也走这个判断
	        if (currentHandle < currentBounds.length - 1 && val >= currentBounds[currentHandle + 1]) {
	            return currentBounds[currentHandle + 1];
	        }
	    }
	    /* eslint-enable eqeqeq */
	    return val;
    }, []);

    // trimAlignValue 函数
    const trimAlignValue = useCallback((v: number, nextProps: any, currentHandle?: number | null, currentBounds?: number[]) => {
	    const mergedConfig = {...mergedProps, ...nextProps};
	    const valInRange = utils.ensureValueInRange(v, mergedConfig);
	    const valNotConflict = ensureValueNotConflict(
	        valInRange,
	        mergedConfig,
	        currentHandle !== undefined ? currentHandle : handle,
	        currentBounds || bounds
	    );
	    return utils.ensureValuePrecision(valNotConflict, mergedConfig);
    }, [mergedProps, handle, bounds, ensureValueNotConflict]);

    // 替换 componentDidUpdate
    const prevPropsRef = useRef<RangeProps>(mergedProps);
    useEffect(() => {
	    const prevProps = prevPropsRef.current;

	    // 只在 value、min、max 变化时执行
	    if (!('value' in mergedProps || 'min' in mergedProps || 'max' in mergedProps)) {
	        prevPropsRef.current = mergedProps;
	        return;
	    }

	    if (
	        (mergedProps.min === prevProps.min) &&
	        (mergedProps.max === prevProps.max) &&
	        shallowEqual(mergedProps.value, prevProps.value)
	    ) {
	        prevPropsRef.current = mergedProps;
	        return;
	    }

	    const value = mergedProps.value || bounds;
	    const nextBounds = value.map((v: number) => trimAlignValue(v, mergedProps, null, bounds));
	    if (nextBounds.length === bounds.length && nextBounds.every((v: number, i: number) => v === bounds[i])) {
	        prevPropsRef.current = mergedProps;
	        return;
	    }

	    setBounds(nextBounds);
	    if (bounds.some((v: number) => utils.isValueOutOfRange(v, mergedProps))) {
	        mergedProps.onChange?.(nextBounds);
	    }

	    prevPropsRef.current = mergedProps;
    }, [mergedProps.value, mergedProps.min, mergedProps.max, trimAlignValue]);

    // onChange 函数
    const onChange = useCallback((state: Partial<RangeState>) => {
	    const isNotControlled = !('value' in mergedProps);
	    if (isNotControlled) {
	        if (state.bounds !== undefined) setBounds(state.bounds);
	        if (state.handle !== undefined) setHandle(state.handle);
	        if (state.recent !== undefined) setRecent(state.recent);
	    } else if (state.handle !== undefined) {
	        setHandle(state.handle);
	    }

	    const changedValue = state.bounds;
	    if (changedValue) {
	        mergedProps.onChange?.(changedValue);
	    }
    }, [mergedProps]);

    // getClosestBound 函数
    const getClosestBound = useCallback((value: number) => {
	    // 注意都是从0开始算，因此handle会重新计算，哪怕当初操作1，再次操作可能会变成0
	    let closestBound = 0;
	    for (let i = 1; i < bounds.length - 1; ++i) {
	        if (value > bounds[i]) {
	            closestBound = i;
	        }
	    }
	    if (Math.abs(bounds[closestBound + 1] - value) < Math.abs(bounds[closestBound] - value)) {
	        closestBound = closestBound + 1;
	    }
	    return closestBound;
    }, [bounds]);

    // getBoundNeedMoving 函数
    const getBoundNeedMoving = useCallback((value: number, closestBound: number) => {
	    let boundNeedMoving = closestBound;
	    const isAtTheSamePoint = (bounds[closestBound + 1] === bounds[closestBound]);
	    if (isAtTheSamePoint) {
	        boundNeedMoving = recent;
	    }

	    if (isAtTheSamePoint && (value !== bounds[closestBound + 1])) {
	        boundNeedMoving = value < bounds[closestBound + 1] ? closestBound : closestBound + 1;
	    }
	    return boundNeedMoving;
    }, [bounds, recent]);

    // onStart 函数
    const onStart = useCallback((position: number) => {
	    // 1.getValue():this.state.bounds
	    // 2.calcValueByPos():同slider.js
	    // 3.getClosestBound()：vs utils.jsx中的getClosestPoints()，返回closestBound为数组坐标
	    // 4.getBoundNeedMoving()：一般返回closestBound;特殊情况就是closeBound=0的情况1确实是0，2.两点重合来判断点落在左侧还是右侧
	    // 受控模式下使用props.value
	    const currentBounds = 'value' in mergedProps ? mergedProps.value! : bounds;
	    mergedProps.onBeforeChange?.(currentBounds);

	    const value = calcValueByPosRef.current(position);

	    const closestBound = getClosestBound(value);
	    const boundNeedMoving = getBoundNeedMoving(value, closestBound);

	    setHandle(boundNeedMoving);
	    setRecent(boundNeedMoving);

	    // console.log('handle是：'+boundNeedMoving+"recent是："+boundNeedMoving)
	    const prevValue = currentBounds[boundNeedMoving];
	    if (value === prevValue) return;

	    const nextBounds = [...currentBounds];
	    nextBounds[boundNeedMoving] = value;
	    onChange({bounds: nextBounds});
    }, [mergedProps, bounds, getClosestBound, getBoundNeedMoving, onChange]);

    // onEnd 函数
    const onEnd = useCallback(() => {
	    if (handle === null) return;
	    setHandle(null);
	    removeDocumentEventsRef.current();
	    mergedProps.onAfterChange?.(bounds);
    }, [handle, mergedProps, bounds]);

    // pushHandleOnePoint 和 pushHandle 相互递归调用，需要特殊处理
    const pushHandleRef = useRef<(bounds: number[], handle: number, direction: number, amount: number) => boolean>();
    const pushSurroundingHandlesRef = useRef<(bounds: number[], handle: number, originalValue: number) => void>();

    // onMove 函数
    const onMove = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>, position: number) => {
	    utils.pauseEvent(e);

	    const value = calcValueByPosRef.current(position);
	    const oldValue = bounds[handle!];
	    if (value === oldValue) return;

	    const nextBounds = [...bounds];
	    nextBounds[handle!] = value;
	    let nextHandle = handle;
	    if (mergedProps.pushable !== false) {
	        const originalValue = bounds[nextHandle!];
	        pushSurroundingHandlesRef.current?.(nextBounds, nextHandle!, originalValue);
	    } else if (mergedProps.allowCross) {
	        nextBounds.sort((a, b) => a - b);
	        nextHandle = nextBounds.indexOf(value);
	    }
	    onChange({
	        handle: nextHandle,
	        bounds: nextBounds,
	    });
    }, [mergedProps, bounds, handle, onChange]);

    /**
	 * Returns an array of possible slider points, taking into account both
	 * `marks` and `step`. The result is cached.
	 */
    const getPoints = useCallback(() => {
	    const {marks, step} = mergedProps;
	    const cache = _getPointsCacheRef.current;
	    if (!cache || cache.marks !== marks || cache.step !== step) {
	        const pointsObject = {...marks};
	        if (step !== null) {
	            for (let point = min; point <= max; point += step) {
	                pointsObject[point] = point;
	            }
	        }
	        const points = Object.keys(pointsObject).map(parseFloat);
	        points.sort((a, b) => a - b);
	        _getPointsCacheRef.current = {marks, step, points};
	    }
	    return _getPointsCacheRef.current?.points;
    }, [mergedProps, min, max]);

    // pushHandleOnePoint 函数
    const pushHandleOnePoint = useCallback((bounds: number[], handle: number, direction: number): boolean => {
	    const points = getPoints();
	    const pointIndex = points?.indexOf(bounds[handle]);
	    const nextPointIndex = pointIndex! + direction;
	    if (nextPointIndex >= points!.length || nextPointIndex < 0) {
	        // reached the minimum or maximum available point, can't push anymore
	        return false;
	    }
	    const nextHandle = handle + direction;
	    const nextValue = points![nextPointIndex];
	    const {pushable: threshold} = mergedProps;
	    const diffToNext = direction * (bounds[nextHandle] - nextValue);
	    if (!pushHandleRef.current!(bounds, nextHandle, direction, threshold - diffToNext)) {
	        // couldn't push next handle, so we won't push this one either
	        return false;
	    }
	    // push the handle
	    bounds[handle] = nextValue;
	    return true;
    }, [mergedProps, getPoints]);

    // pushHandle 函数
    const pushHandle = useCallback((bounds: number[], handle: number, direction: number, amount: number): boolean => {
	    const originalValue = bounds[handle];
	    let currentValue = bounds[handle];
	    while (direction * (currentValue - originalValue) < amount) {
	        if (!pushHandleOnePoint(bounds, handle, direction)) {
	            // can't push handle enough to create the needed `amount` gap, so we
	            // revert its position to the original value
	            bounds[handle] = originalValue;
	            return false;
	        }
	        currentValue = bounds[handle];
	    }
	    // the handle was pushed enough to create the needed `amount` gap
	    return true;
    }, [pushHandleOnePoint]);

    // pushSurroundingHandles 函数
    const pushSurroundingHandles = useCallback((bounds: number[], handle: number, originalValue: number) => {
	    const {pushable: threshold} = mergedProps;
	    const value = bounds[handle];

	    let direction = 0;
	    if (bounds[handle + 1] - value < threshold) {
	        direction = +1; // push to right
	    }
	    if (value - bounds[handle - 1] < threshold) {
	        direction = -1; // push to left
	    }

	    if (direction === 0) {
	        return;
	    }

	    const nextHandle = handle + direction;
	    const diffToNext = direction * (bounds[nextHandle] - value);
	    if (!pushHandleRef.current!(bounds, nextHandle, direction, threshold - diffToNext)) {
	        // revert to original value if pushing is impossible
	        bounds[handle] = originalValue;
	    }
    }, [mergedProps]);

    // 设置 ref
    pushHandleRef.current = pushHandle;
    pushSurroundingHandlesRef.current = pushSurroundingHandles;


    // 更新 HOC 注入的方法到 ref 中
    useEffect(() => {
        if (injectedCalcValueByPos) {
            calcValueByPosRef.current = injectedCalcValueByPos;
        }
        if (injectedCalcOffset) {
            calcOffsetRef.current = injectedCalcOffset;
        }
        if (injectedRemoveDocumentEvents) {
            removeDocumentEventsRef.current = injectedRemoveDocumentEvents;
        }
    }, [injectedCalcValueByPos, injectedCalcOffset, injectedRemoveDocumentEvents]);


    // Render 逻辑
    const {
	    prefixCls,
	    vertical,
	    included,
	    disabled,
	    handle: handleGenerator,
	    trackStyle,
	    reverse,
	    handleStyle,
	    dir: direction
    } = mergedProps;

    // 受控模式下使用props.value，避免状态更新延迟
    const currentBounds = 'value' in mergedProps ? mergedProps.value! : bounds;
    const offsets = currentBounds.map((v: number) => calcOffsetRef.current(v));

    const handleClassName = `${prefixCls}-handle`;
    const handles = currentBounds.map((v: number, i: number) => (handleGenerator || Handle.defaultHandle)({
	    className: classNames({
	        [handleClassName]: true,
	        [`${handleClassName}-${i + 1}`]: true,
	    }),
	    vertical,
	    reverse,
	    dir: direction,
	    offset: offsets[i],
	    value: v,
	    dragging: handle === i,
	    index: i,
	    min,
	    max,
	    disabled,
	    style: handleStyle[i],
	    ref: (h: any) => {
	        handleRefs.current[i] = h;
	    },
    }));

    const tracks = currentBounds.slice(0, -1).map((_: number, index: number) => {
	    const i = index + 1;
	    const trackClassName = classNames({
	        [`${prefixCls}-track`]: true,
	        [`${prefixCls}-track-${i}`]: true,
	    });
	    return (
	        <Track
	            className={trackClassName}
	            vertical={vertical}
	            reverse={reverse}
	            dir={direction}
	            included={included}
	            offset={offsets[i - 1]}
	            length={offsets[i] - offsets[i - 1]}
	            style={trackStyle[index]}
	            key={i}
	        />
	    );
    });

    // 使用 ref 存储 bounds 和 handle，tracks/handles直接在useImperativeHandle中返回
    const boundsRef = useRef(bounds);
    const handleRef = useRef(handle);

    useEffect(() => {
        boundsRef.current = bounds;
        handleRef.current = handle;
    }, [bounds, handle]);

    // 暴露额外的方法给 HOC
    useImperativeHandle(ref, () => ({
        calcValueByPos: (value: number) => calcValueByPosRef.current(value),
        calcOffset: (value: number) => calcOffsetRef.current(value),
        saveHandle: (index: number, h: any) => {
            handleRefs.current[index] = h;
        },
        removeDocumentEvents: () => removeDocumentEventsRef.current(),
        // 受控模式下直接返回props.value，避免状态更新延迟
        getValue: () => 'value' in mergedProps ? mergedProps.value! : boundsRef.current,
        getLowerBound: () => {
            const currentBounds = 'value' in mergedProps ? mergedProps.value! : boundsRef.current;
            return currentBounds[0];
        },
        getUpperBound: () => {
            const currentBounds = 'value' in mergedProps ? mergedProps.value! : boundsRef.current;
            return currentBounds[currentBounds.length - 1];
        },
        trimAlignValue, // 添加 trimAlignValue 方法
        onKeyboard: (e: React.KeyboardEvent<HTMLDivElement>) => {
            const currentHandle = handleRef.current;
            const currentBounds = boundsRef.current;
            if (currentHandle === null) return;

            const valueMutator = utils.getKeyboardValueMutator(e);
            if (!valueMutator) return;

            utils.pauseEvent(e);
            const oldValue = currentBounds[currentHandle];
            const mutatedValue = valueMutator(oldValue, mergedProps);
            const newValue = trimAlignValue(mutatedValue, mergedProps, currentHandle, currentBounds);

            if (newValue === oldValue) return;

            const nextBounds = [...currentBounds];
            nextBounds[currentHandle] = newValue;

            // 处理 pushable 和 allowCross
            if (mergedProps.pushable !== false) {
                pushSurroundingHandlesRef.current?.(nextBounds, currentHandle, oldValue);
            } else if (mergedProps.allowCross) {
                nextBounds.sort((a, b) => a - b);
                const nextHandle = nextBounds.indexOf(newValue);
                onChange({
                    handle: nextHandle,
                    bounds: nextBounds,
                });
                return;
            }

            onChange({bounds: nextBounds});
        },
        onStart,
        onMove,
        onEnd,
        // 关键修复：直接返回最新计算的 tracks 和 handles
        // 而不是通过 ref（ref会因为useEffect异步执行导致延迟）
        tracks,
        handles
    }), [
        trimAlignValue,
        onStart, onMove, onEnd, tracks, handles
    ]);

    // Range 组件本身不渲染任何内容，渲染由 createSlider HOC 处理
    return null;
});

// 设置静态属性
Range.displayName = 'Range';

export default createSlider(Range as any) as React.ComponentClass<Partial<RangeProps>>;
